import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { copyRichTextToClip } from 'core/utils';
import { openToastr } from '@Common/handlers/services';
import { CloseOutlined } from '@mui/icons-material';
import { generateCitations } from 'src/researcher/handlers/api';
import citationGeneratorTrack from '../handlers/track';
import { CitationData, Contributor, ContributorTypes, FormattedCitationContent } from '../types';
import { InTextModalField } from './EditForm/components';
import { CitationElement } from './CitationElement';
import { TriggeringActions } from '../types/names';
import { getCgBreakpointDown } from '../utils';

const useStyles = makeStyles((theme) => ({
  inTextPaperWidthSm: {
    maxWidth: '560px',
    maxHeight: '350px',
    height: '350px',
    width: '560px',
    padding: '0px 20px',
  },
  checkBoxLabel: {
    [theme.breakpoints.down(360)]: {
      fontSize: '12px',
    },
  },
  checked: {
    color: theme.specialColors.BLUE_COLOR,
    '&:hover': {
      color: theme.specialColors.BLUE_COLOR,
    },
  },

  checkBoxIcon: {
    padding: '6px ',
    [theme.breakpoints.down(360)]: {
      paddingRight: 0,
    },
    '&:hover': {
      color: theme.specialColors.BLUE_COLOR,
    },
  },
  checkBoxPrimary: {
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  labelRoot: {
    marginLeft: 5,
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
    },
    [theme.breakpoints.down(360)]: {
      marginRight: 0,
    },
  },
}));

const useExportDialogStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 600,
    fontSize: '14px',
  },
  dialogContent: {
    borderRadius: '4px',
    height: 240,
    width: '100%',
    overflow: 'auto',
    wordBreak: 'break-all',
  },
  marginBotton: {
    marginBottom: 10,
  },
  marginBotton2: {
    marginBottom: 20,
  },
  checkboxFieldsWrapper: {
    marginTop: 13,
    [getCgBreakpointDown(theme)]: {
      marginTop: 0,
    },
  },
  textFieldsWrapper: {
    // width: 'calc(87% - 10px)',
    paddingRight: 15,
    paddingLeft: 5,
  },
}));

const checkboxField = (
  isChecked: boolean,
  onChangeHandler: Function,
  fieldValue: string | number
) => {
  const dialogClasses = useStyles();
  const inTextdialogClasses = useExportDialogStyles();

  return (
    <Grid container className={inTextdialogClasses.marginBotton2}>
      <FormControlLabel
        classes={{
          root: dialogClasses.labelRoot,
          label: dialogClasses.checkBoxLabel,
        }}
        control={
          <Checkbox
            color="primary"
            checked={isChecked}
            onChange={() => onChangeHandler(!isChecked)}
            value={fieldValue}
            className={`${dialogClasses.checkBoxIcon} ${dialogClasses.checkBoxPrimary}`}
            classes={{ checked: dialogClasses.checked }}
          />
        }
        label=""
      />
    </Grid>
  );
};

const textField = (
  onFieldValueChangeHandler: Function,
  fieldValue: string | number,
  label: string,
  key: string
) => {
  const obj: { [key: string]: string | number } = {};
  obj[key] = fieldValue;
  const inTextdialogClasses = useExportDialogStyles();

  return (
    <Grid container className={inTextdialogClasses.marginBotton}>
      <InTextModalField
        label={label}
        value={obj}
        fields={[{ label, key, highlightOnEdit: false }]}
        onChange={(newValue: any) => onFieldValueChangeHandler(newValue[key])}
      />
    </Grid>
  );
};

const joinNonEmpty = (entries: Array<string | number | undefined>, joinChar: string) =>
  entries.filter(Boolean).join(joinChar);
const getAuthors = (contributors: CitationData['contributors']) =>
  contributors
    .filter((contributor) => contributor.contributorType === ContributorTypes.AUTHORS)
    .map((contributor) =>
      joinNonEmpty([contributor.firstName || contributor.initials, contributor.lastName], ' ')
    )
    .join('; ')
    .trim();

type InTextCitation = FormattedCitationContent[number] & {
  isIdentifier?: boolean;
};

const InTextModal = ({
  citationStyle,
  citation,
  onClose,
  shortCitation,
  citationNumber,
}: {
  citationStyle: string;
  citation: CitationData;
  onClose: Function;
  shortCitation: InTextCitation[];
  citationNumber?: number;
}) => {
  const dialogClasses = useStyles();
  const inTextdialogClasses = useExportDialogStyles();

  const [useContributors, setUseContributors] = useState(true);
  const [contributors, setContributors] = useState(getAuthors(citation.contributors) || '');
  const [useTitle, setUseTitle] = useState(true);
  const [title, setTitle] = useState(citation.publicationInfo?.title || '');
  const [useYear, setUseYear] = useState(true);
  const [year, setYear] = useState(citation.publicationInfo?.publishDate?.year || '');
  const [useIdentifiers, setUseIdentifiers] = useState(false);
  const [identifiers, setIdentifiers] = useState('');

  const inTextCitationRef = useRef<HTMLDivElement>(null);
  const [inTextCitation, setIntextCitation] = useState(shortCitation);
  const [isLoading, setIsLoading] = useState(false);

  const didMount = useRef(false);

  const renderFields = [
    {
      isChecked: useContributors,
      onChangeHandler: setUseContributors,
      onFieldValueChangeHandler: setContributors,
      value: contributors,
      label: 'Contributors',
      key: 'contributors',
    },
    {
      isChecked: useTitle,
      onChangeHandler: setUseTitle,
      onFieldValueChangeHandler: setTitle,
      value: title,
      label: 'Title',
      key: 'title',
    },
    {
      isChecked: useYear,
      onChangeHandler: setUseYear,
      onFieldValueChangeHandler: setYear,
      value: year,
      label: 'Year',
      key: 'year',
    },
    {
      isChecked: useIdentifiers,
      onChangeHandler: setUseIdentifiers,
      onFieldValueChangeHandler: setIdentifiers,
      value: identifiers,
      label: 'Identifiers',
      key: 'identifiers',
    },
  ];

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return () => {};
    }

    let isLatest = true;
    setIsLoading(true);
    const currCitation: CitationData = {
      ...citation,
      contributors: [],
      publicationInfo: {
        ...citation.publicationInfo,
        title: '',
        publishDate: {
          ...(citation.publicationInfo.publishDate || {}),
          year: '',
        },
      },
    };

    if (useContributors && contributors) {
      if (contributors.trim() === getAuthors(citation.contributors)) {
        // copying the initial contributors here because of same input
        currCitation.contributors = citation.contributors;
      } else {
        currCitation.contributors = contributors
          .trim()
          .split(/\s?;\s?/)
          .map((name) => {
            const contributor: Contributor = { contributorType: ContributorTypes.AUTHORS, key: '' };
            const [first, second, ...remaining] = name.trim().split(/\s+/);
            if (remaining.length) {
              contributor.lastName = remaining.filter(Boolean).join(' ');
              contributor.firstName = [first, second].filter(Boolean).join(' ');
            } else if (second) {
              contributor.lastName = second;
              contributor.firstName = first;
            } else if (first) {
              contributor.literal = first;
            }
            return contributor;
          });
      }
    }

    if (useTitle && title) {
      currCitation.publicationInfo.title = title.trim();
    }

    if (useYear && (year || year === 0)) {
      if (!currCitation.publicationInfo.publishDate) {
        currCitation.publicationInfo.publishDate = {};
      }
      currCitation.publicationInfo.publishDate.year = year.toString().trim();
    }

    generateCitations({ style: citationStyle, citations: [currCitation] })
      .then((response) => {
        if (isLatest) {
          setIntextCitation(response.data[0].shortCitation);
        }
      })
      .finally(() => {
        if (isLatest) {
          setIsLoading(false);
        }
      });

    return () => {
      isLatest = false;
    };
  }, [useContributors, useTitle, useYear, contributors, title, year]);

  useEffect(() => {
    if (useIdentifiers && identifiers) {
      setIntextCitation((prevValue) => {
        let shouldAdd = true;
        const text = `(${identifiers})`;
        const newValue = prevValue.map((part) => {
          if (part.isIdentifier) {
            part.text = text;
            shouldAdd = false;
          }
          return part;
        });

        if (shouldAdd) {
          newValue.push({ text, isIdentifier: true });
        }

        return newValue;
      });
    } else {
      setIntextCitation((prevValue) => prevValue.filter((part) => !part.isIdentifier));
    }
  }, [useIdentifiers, identifiers]);

  return (
    <Dialog
      open
      classes={{
        paperWidthSm: dialogClasses.inTextPaperWidthSm,
      }}
      style={{
        borderRadius: '8px',
        backdropFilter: 'blur(3px)',
      }}
    >
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        style={{
          height: '48px',
        }}
      >
        <IconButton
          onClick={() => {
            onClose();
            // citationGeneratorTrack.userClosedPreviewModal();
          }}
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            padding: 2,
          }}
        >
          <CloseOutlined fontSize="small" />
        </IconButton>

        <Typography className={inTextdialogClasses.title}>In-Text Citation</Typography>
      </Grid>

      <Grid
        container
        className={inTextdialogClasses.dialogContent}
        style={{
          height: '181px',
        }}
      >
        <Grid item xs={1.5} md={1} className={inTextdialogClasses.checkboxFieldsWrapper}>
          {renderFields.map((field) =>
            checkboxField(field.isChecked, field.onChangeHandler, field.value)
          )}
        </Grid>

        <Grid item xs={10.5} md={11} className={inTextdialogClasses.textFieldsWrapper}>
          {renderFields.map((field) =>
            textField(field.onFieldValueChangeHandler, field.value, field.label, field.key)
          )}
        </Grid>
      </Grid>

      <Divider />

      <Grid
        container
        justifyContent="space-between"
        alignItems="flex-end"
        style={{
          marginTop: 12,
          marginBottom: '16px',
        }}
      >
        <Grid item style={{ minWidth: '85%', maxWidth: '85%' }}>
          <Grid container alignItems="center">
            <Typography style={{ fontSize: '11px', marginRight: '5px' }}>Preview</Typography>

            {isLoading && <CircularProgress size={15} />}
          </Grid>

          <Grid
            container
            style={{
              backgroundColor: '#F5F6F8',
              minHeight: '70px',
              maxHeight: '70px',
              overflow: 'auto',
              borderRadius: '4px',
              marginTop: '5px',
            }}
            ref={inTextCitationRef}
          >
            <Typography
              style={{ fontSize: '11px', padding: '5px 10px 5px 10px', wordBreak: 'break-all' }}
            >
              <CitationElement content={inTextCitation} citationNumber={citationNumber} />
            </Typography>
          </Grid>
        </Grid>

        <Grid item>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              if (isLoading) return;

              try {
                citationGeneratorTrack.userClickedCopyInTextCitationButton(
                  citationStyle,
                  TriggeringActions.IN_TEXT_PREVIEW_MODAL
                );
                copyRichTextToClip(
                  inTextCitationRef?.current?.innerHTML || '',
                  () => openToastr('In-text citation copied to clipboard!'),
                  () => openToastr('Failed to copy! Please copy manually!')
                );
              } catch (error) {
                console.log('Error in Copying', error);
              }
            }}
            style={{
              fontWeight: 600,
              height: '30px',
              borderRadius: '50px',
            }}
          >
            <Grid container justifyContent="center" alignItems="center">
              {isLoading ? <CircularProgress size="18px" color="inherit" /> : 'Copy'}
            </Grid>
          </Button>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default InTextModal;
